import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Drawer, DrawerContent } from '@heroui/react'
import { RefundAction } from '@itsrever/returns-api-types'
import type { ReturnsLineItem } from '@itsrever/rever-api'
import { useTranslate } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useTheme } from '@/ui/theme'
import { XMarkIcon } from '@/ui/icons/xmark'
import { MediaImageIcon } from '@/ui/icons/media-image'
import {
  ExchangedItemType,
  removeExchangeSelectionsByType,
  removeReturnedItem
} from '@/redux/return/return-slice'
import { calculateUnitPrice } from '../helpers/calculate-unit-price'
import { ItemFlow } from '../item-flow/item-flow'

interface HoffItemCardProps {
  lineItem: ReturnsLineItem
  subProductId: number
}

export const HoffItemCard = ({ lineItem, subProductId }: HoffItemCardProps) => {
  const { translate } = useTranslate()

  const { i18n } = useTranslation()

  const theme = useTheme()

  const dispatch = useAppDispatch()

  const [searchParams, setSearchParams] = useSearchParams()

  const [open, setOpen] = useState(false)

  const isFullCatalogOpen = searchParams.get('catalog') === 'true'

  const itemsToReturn = useAppSelector((store) => store.return.returnedItems)

  const exchangeSelections = useAppSelector((store) => store.return.newItems)

  const moneyFormat = useAppSelector(
    (store) => store.returnsApi.order.response?.order?.money_format
  )

  const image = lineItem.product?.images?.[0]

  const price = calculateUnitPrice(
    { quantity: lineItem.quantity, total: lineItem.total },
    { amount_multiplied_by: moneyFormat?.amount_multiplied_by }
  )

  const returnedItem = itemsToReturn.find(
    (retItem) =>
      retItem.item.line_item_id === lineItem.id &&
      retItem.subProductId === subProductId
  )

  const isBeingReturned = !!returnedItem

  const isExchange =
    returnedItem?.item.action === RefundAction.RefundAction_TO_EXCHANGE

  const itemExchanges = exchangeSelections.filter(
    (item) => item.returnedItemId === returnedItem?.lineItemId
  )

  const variantExchange = itemExchanges.find(
    (item) => item.exchangeType === ExchangedItemType.ONE_ON_ONE
  )

  const fullCatalogExchanges = itemExchanges.filter(
    (item) => item.exchangeType === ExchangedItemType.FULL_CATALOG
  )

  const openItemFlow = (open: boolean) => {
    setOpen(open)

    if (!open && !isFullCatalogOpen) {
      searchParams.delete('lineItemId')
      setSearchParams(searchParams)

      return
    }

    searchParams.set('lineItemId', lineItem.id ?? '')
    setSearchParams(searchParams)
  }

  const onRemoveReturn = () => {
    if (returnedItem?.lineItemId) {
      dispatch(removeReturnedItem({ lineItemId: returnedItem.lineItemId }))
    }
  }

  const onRemoveExchange = (exchangeType: ExchangedItemType) => {
    if (returnedItem?.lineItemId) {
      dispatch(
        removeExchangeSelectionsByType({
          lineItemId: returnedItem.lineItemId,
          exchangeType
        })
      )
    }
  }

  return (
    <>
      <div
        onClick={() => {
          if (!isBeingReturned) openItemFlow(true)
        }}
        className="relative flex w-full cursor-pointer flex-col border-t-1 border-solid border-black bg-white p-2 transition-all hover:bg-[#F3F3F3] lg:flex-row lg:items-center lg:justify-between"
      >
        <div className="flex w-full items-center gap-2 sm:gap-3">
          <div className="flex w-full flex-row items-center gap-2 sm:gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              <div className="relative my-2 h-24 w-16 shrink-0 overflow-hidden rounded-lg sm:h-28 sm:w-20 md:h-32 md:w-24">
                {image ? (
                  <img
                    src={image.src}
                    alt={image.alt ?? lineItem.name ?? 'Product'}
                    className="absolute inset-0 size-full rounded-md object-cover"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center bg-neutral-50 text-neutral-500">
                    <MediaImageIcon width={20} height={20} />
                  </div>
                )}
              </div>
              <div className="flex min-w-0 flex-col justify-start gap-1.5 sm:gap-3">
                <p className="line-clamp-2 text-xs font-normal uppercase text-neutral-800">
                  {lineItem.name}
                </p>
                <p className="truncate text-xs font-light text-black">
                  {lineItem.variant_name}
                </p>
              </div>
            </div>
            <div className="w-6 shrink-0 text-center sm:w-10">
              <p className="text-sm font-normal">{lineItem.quantity}</p>
            </div>
            <div className="w-16 shrink-0 text-right sm:w-24">
              <p className="whitespace-nowrap text-sm font-normal">
                {price.toLocaleString(i18n.language, {
                  style: 'currency',
                  currency: moneyFormat?.currency
                })}
              </p>
            </div>
          </div>
          {!isBeingReturned ? (
            <svg
              width="24px"
              height="24px"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              color="#000000"
              aria-hidden
              className="shrink-0"
            >
              <path
                d="M9 6L15 12L9 18"
                stroke="#000000"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          ) : null}
        </div>

        {isBeingReturned ? (
          isExchange ? (
            <div
              data-testid="selected-box"
              className="flex w-full grow flex-wrap items-center justify-end gap-x-3 gap-y-2"
            >
              {variantExchange ? (
                <div className="flex items-center gap-2">
                  <p className="max-w-32 shrink truncate text-xs text-black">
                    <span className="mr-2 font-medium">NEW</span>
                    {variantExchange.productVariant.variantName ?? ''}
                  </p>
                  <button
                    aria-label="Remove one-on-one exchange"
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemoveExchange(ExchangedItemType.ONE_ON_ONE)
                    }}
                    className="relative flex size-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-neutral-100 before:absolute before:-inset-1.5 before:content-['']"
                  >
                    <XMarkIcon color={theme.colors.grey[1]} className="size-4" />
                  </button>
                </div>
              ) : null}
              {fullCatalogExchanges.length > 0 ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="shrink-0 text-xs font-medium">NEW</span>
                    <ul className="flex items-center gap-1">
                      {fullCatalogExchanges.slice(0, 3).map((item, i) => (
                        <li
                          key={i}
                          className="size-14 shrink-0 overflow-hidden rounded-md bg-neutral-50"
                        >
                          {item.productVariant.imageUrl ? (
                            <img
                              className="size-full object-cover"
                              src={item.productVariant.imageUrl}
                              alt={
                                item.productVariant.productName ??
                                item.productVariant.variantName ??
                                'Exchange item'
                              }
                              width={56}
                              height={56}
                            />
                          ) : (
                            <span className="flex size-full items-center justify-center text-neutral-500">
                              <MediaImageIcon width={18} height={18} />
                            </span>
                          )}
                        </li>
                      ))}
                      {fullCatalogExchanges.length > 3 ? (
                        <li className="flex size-14 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-xs font-medium text-neutral-700">
                          +{fullCatalogExchanges.length - 3}
                        </li>
                      ) : null}
                    </ul>
                  </div>
                  <button
                    aria-label="Remove full catalog exchange items"
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemoveExchange(ExchangedItemType.FULL_CATALOG)
                    }}
                    className="relative flex size-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-neutral-100 before:absolute before:-inset-1.5 before:content-['']"
                  >
                    <XMarkIcon color={theme.colors.grey[1]} className="size-4" />
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div
                data-testid="selected-box"
                className="flex w-full grow justify-end lg:h-auto lg:content-center lg:items-center"
              >
                <div className="mr-2 flex items-center gap-1">
                  <span
                    className="mr-2 truncate text-sm font-medium text-neutral-800"
                    dangerouslySetInnerHTML={{
                      __html: translate('icons_return')
                    }}
                  />
                </div>
              </div>
              <button
                id="crossbox"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemoveReturn()
                }}
                className="absolute right-1 top-2 mx-1 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md lg:relative lg:right-0 lg:top-0"
              >
                <XMarkIcon color={theme.colors.grey[1]} className="size-4" />
              </button>
            </>
          )
        ) : null}
      </div>
      <Drawer
        isOpen={open}
        onOpenChange={openItemFlow}
        placement="left"
        radius="md"
        hideCloseButton
        classNames={{
          wrapper: 'h-dvh max-h-dvh',
          backdrop: 'bg-black/30',
          base: 'rounded-none md:bottom-5 md:left-5 md:top-5 md:rounded-2xl w-full max-w-full md:max-w-[480px] h-dvh max-h-dvh md:h-auto pb-2 md:pb-0'
        }}
      >
        <DrawerContent>
          {(onClose) => (
            <ItemFlow
              lineItem={lineItem}
              subProductId={subProductId}
              onClose={onClose}
            />
          )}
        </DrawerContent>
      </Drawer>
    </>
  )
}
